import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { rawFetch } from "@/lib/api/client";

export interface TermSetEntry {
  id: string;
  name: string;
  latest_version_id: string | null;
  versions: Array<{ id: string; number: number; digest: string; entry_count: number;
    tags: Record<string, string[]>; used_by?: { drafts: string[]; profiles: string[] } }>;
}

export function useTermSets(workspace: string | null) {
  return useQuery<{ count: number; results: TermSetEntry[] }>({
    queryKey: ["term-sets", workspace],
    queryFn: () => rawFetch(`/ontology/term-sets/?workspace=${encodeURIComponent(workspace!)}&limit=100`),
    enabled: Boolean(workspace),
  });
}

export function TermSetSelector({ workspace, selected, onChange, disabled = false }: {
  workspace: string | null;
  selected: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
}) {
  const query = useTermSets(workspace);
  return <section className="rounded border border-border bg-bg/60 p-3" aria-label="Approved term-set versions">
    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-text">Approved term sets</div>
        <p className="text-xs text-muted">Pinned: this profile always uses exactly these versions. Editing a set creates a new version.</p>
      </div>
      <Link to="/ontology/term-sets" className="text-xs text-accent underline">Open library</Link>
    </div>
    {query.isLoading && <p className="text-xs text-muted">Loading term sets…</p>}
    {query.isError && <p className="text-xs text-bad" role="alert">Could not load term sets.</p>}
    {query.data?.results.length === 0 && <p className="text-xs text-muted">No approved term sets in this workspace yet.</p>}
    <div className="grid gap-2 sm:grid-cols-2">
      {query.data?.results.flatMap((set) => set.versions.map((version) => {
        const checked = selected.includes(version.id);
        return <label key={version.id} className="flex cursor-pointer items-start gap-2 rounded border border-border p-2 text-xs text-text">
          <input type="checkbox" className="mt-0.5 accent-accent" checked={checked} disabled={disabled}
            onChange={() => onChange(checked ? selected.filter((id) => id !== version.id) : [...selected, version.id])} />
          <span><strong>{set.name}</strong> · v{version.number} · {version.entry_count} terms
            {checked && set.latest_version_id !== version.id && <span className="block text-[11px] text-warn">Newer version available: review and re-pin when ready.</span>}
          </span>
        </label>;
      }))}
    </div>
  </section>;
}
